import { useEffect, useState } from "react";
import { Link, Route } from "react-router-dom";
import { Home as HomeIcon, LayoutGrid, BookOpen, Moon, ArrowUpRight, MessageCircleHeart, Wind, Activity } from "lucide-react";
import { api, useAuth } from "@/lib/auth";
import { GATES } from "@/data/temple";
import { APPS } from "@/lib/apps";
import { SPACES, SpaceContext } from "@/lib/space";
import { useQuantumJourney } from "@/lib/quantum";
import { useJourney, gateProgress } from "@/lib/journey";
import GatePage from "@/pages/GatePage";
import Journey from "@/pages/Journey";
import QuantumGate from "@/components/quantum/QuantumGate";
import GateMap from "@/components/quantum/GateMap";
import { VoiceButton } from "@/components/quantum/TeachingCards";
import { FieldView, GateList, NextGate, TodayCard } from "@/components/app/GateJourney";
import { MoonCalendar } from "@/components/temple/IntentionJournal";
import { DEFAULTS, Gold, moonTonight } from "@/components/temple/MoonRitual";
import { AppFrame, Page, ghost } from "@/components/app/AppKit";
import InstallGuide from "@/components/app/InstallGuide";
import ArielChat from "@/components/app/ArielChat";
import AnkhBreath from "@/components/app/AnkhBreath";
import DailyWhisper from "@/components/app/DailyWhisper";
import GatesBodyMap from "@/components/app/GatesBodyMap";
import { PartnerBreathLobby, PartnerBreathSession } from "@/components/app/PartnerBreath";
import { BreathMapsJournal } from "@/components/app/CoupleMap";
import ProgressPanel from "@/components/app/ProgressPanel";
import OurField from "@/components/app/OurField";

const pad = (n) => String(n).padStart(2, "0");

const TABS = [
  ["", "Home", HomeIcon],
  ["gates", "Gates", LayoutGrid],
  ["field", "Field", Activity],
  ["breath", "Breath", Wind],
  ["journal", "Journal", BookOpen],
  ["ariel", "Ariel", MessageCircleHeart],
];

const TonightCard = () => {
  const kind = moonTonight();
  if (!kind) return null;
  return (
    <button data-testid="app-tonight" onClick={() => window.dispatchEvent(new CustomEvent("tt:moon-ritual", { detail: kind }))} className="mt-6 w-full text-left flex items-center gap-4 border border-[#D9B47A]/30 bg-[#120C0A] p-5 hover:border-[#D9B47A]/70 transition-colors">
      <span className={`w-8 h-8 rounded-full shrink-0 ${kind === "full" ? "bg-[#F2E8DF] shadow-[0_0_18px_rgba(242,232,223,0.5)]" : "border border-[#D9B47A]/60"}`} />
      <span className="min-w-0">
        <span className="block font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">{kind === "full" ? "Full" : "New"} Moon tonight</span>
        <span className="block mt-1 font-display text-xl">Begin tonight’s practice</span>
      </span>
      <ArrowUpRight className="ml-auto w-5 h-5 text-[#D9B47A]" />
    </button>
  );
};

const AppHome = () => {
  const { user } = useAuth();
  const { data } = useJourney();
  const { gates, done } = useQuantumJourney();
  const total = GATES.reduce((n, _, i) => n + gateProgress(data[i]), 0);
  const nextIdx = gates.find((x) => x.published && !x.locked && !x.done)?.g ?? 0;
  return (
    <Page testId="app-home" eyebrow={`Welcome back${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`} title={<>Breathe. You are <em className="text-[#D9B47A]">home.</em></>}>
      <DailyWhisper gate={nextIdx} />
      <TodayCard />
      <NextGate />
      <div className="mt-6 grid grid-cols-2 gap-3">
        <div className="border border-[#D9B47A]/15 p-5">
          <p data-testid="app-mastered" className="font-display text-4xl text-[#D9B47A]">{done.length}<span className="text-lg text-[#8F8176]">/13</span></p>
          <p className="mt-1 text-sm text-[#A8998C]">gates sealed</p>
        </div>
        <div className="border border-[#D9B47A]/15 p-5">
          <p data-testid="app-weeks" className="font-display text-4xl text-[#D9B47A]">{total}<span className="text-lg text-[#8F8176]">/39</span></p>
          <p className="mt-1 text-sm text-[#A8998C]">weeks of practice</p>
        </div>
      </div>
      {gates.length > 0 && <GateMap gates={gates} />}
      <div className="mt-6 grid grid-cols-2 gap-3">
        {[["ariel", "Talk with Ariel", "Your guide, by candlelight", MessageCircleHeart], ["breath", "Ankhing the Breath", "A guided circle of breath", Wind], ["moon", "Moon practices", "New and full moon rituals", Moon], ["journal", "Your journals", "Practice and intentions", BookOpen]].map(([to, t, s, Icon]) => (
          <Link key={to} to={`/app/${to}`} data-testid={`app-tile-${to}`} className="border border-[#D9B47A]/20 p-5 hover:border-[#D9B47A]/60 hover:bg-[#120C0A] transition-colors">
            <Icon className="w-5 h-5 text-[#D9B47A]" strokeWidth={1.5} />
            <p className="mt-4 font-display text-xl leading-tight">{t}</p>
            <p className="mt-1 text-[13px] text-[#A8998C]">{s}</p>
          </Link>
        ))}
      </div>
      <Link to="/app/body" data-testid="app-tile-body" className="group mt-6 relative flex items-center gap-5 overflow-hidden border border-[#D9B47A]/25 bg-[#0D0908] p-5 hover:border-[#D9B47A]/70 transition-colors">
        <img src="/images/gates-body-female.webp" alt="" aria-hidden className="w-16 h-24 object-cover object-top shrink-0 opacity-90 transition-transform duration-700 group-hover:scale-105" />
        <span className="min-w-0">
          <span className="block font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">The body map</span>
          <span className="block mt-1 font-display text-xl leading-tight">Read your body in eight layers</span>
          <span className="block mt-1 text-[13px] text-[#A8998C]">Physical to quantum, area by area</span>
        </span>
        <ArrowUpRight className="ml-auto w-5 h-5 shrink-0 text-[#D9B47A] transition-transform duration-500 group-hover:rotate-45" />
      </Link>
      <TonightCard />
      <div className="mt-6"><MoonCalendar /></div>
      <div className="mt-10"><InstallGuide app={APPS.gates} /></div>
    </Page>
  );
};

const AppGates = () => (
  <Page testId="app-gates" eyebrow="The Orgasm Matrix" title={<>The 13 <em className="text-[#D9B47A]">Gates</em></>}>
    <Link to="/app/body" data-testid="app-gates-body-map" className={`${ghost} mt-6`}>Open the body map <ArrowUpRight className="w-4 h-4" /></Link>
    <GateList />
  </Page>
);

const BADGES = [
  ["first", "First practice", (f, t) => t.total_days >= 1],
  ["streak7", "Seven-day flame", (f, t) => t.streak >= 7],
  ["seal3", "Three seals", (f, t, d) => d >= 3],
  ["maps10", "Ten body maps", (f) => f.entries.filter((e) => Object.keys(e.after || {}).length).length >= 10],
  ["streak30", "Thirty-day flame", (f, t) => t.streak >= 30],
  ["seal13", "All 13 sealed", (f, t, d) => d >= 13],
];

const AppField = () => (
  <Page testId="app-field" eyebrow="Your body" title={<>Where pleasure <em className="text-[#D9B47A]">lives.</em></>}>
    <p className="mt-4 text-[15px] text-[#CFC2B6]">Every body map you make after a practice gathers here, so you can watch each gate awaken in you over time.</p>
    <FieldView badges={BADGES} />
  </Page>
);

// Gate reflections and Ariel's replies, kept in the journal.
const GatesKept = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/gates13/kept").then(({ data }) => setList(Array.isArray(data) ? data.filter((d) => d.items?.length || d.reflect_reply || d.checkin?.reply) : [])).catch(() => {});
  }, []);
  if (!list.length) return null;
  return (
    <section data-testid="gates-kept" className="max-w-xl mx-auto px-5 mt-12 border border-[#D9B47A]/20 p-5">
      <p className="font-display text-2xl">Your gate reflections</p>
      {list.map((d) => (
        <details key={d.g} data-testid={`gates-kept-${d.g}`} className="mt-3 border-t border-[#D9B47A]/10 pt-3">
          <summary className="cursor-pointer font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A]">Gate {pad(d.g + 1)} · {d.gate}</summary>
          {d.items.map((it, k) => <div key={k} className="mt-3"><p className="text-[13px] text-[#A8998C]">{it.q}</p><p className="mt-1 text-[15px] text-[#F2E8DF]">{it.a}</p></div>)}
          {[d.reflect_reply, d.checkin?.reply].filter(Boolean).map((t, k) => (
            <div key={`r${k}`} className="mt-4 border-l-2 border-[#E8838E] pl-4">
              <VoiceButton text={t} testId={`gates-kept-voice-${d.g}-${k}`} />
              <p className="mt-2 font-display italic text-[17px] leading-relaxed text-[#F2E8DF]">{t}</p>
            </div>
          ))}
        </details>
      ))}
    </section>
  );
};

const Practice = ({ kind }) => {
  const [p, setP] = useState(DEFAULTS[kind]);
  useEffect(() => {
    let alive = true;
    api.get(`/moon-practice/${kind}`)
      .then(({ data }) => alive && Array.isArray(data?.steps) && data.steps.length && setP({ heading: data.heading ?? DEFAULTS[kind].heading, steps: data.steps, farewell: data.farewell ?? DEFAULTS[kind].farewell }))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [kind]);
  return (
    <section data-testid={`app-practice-${kind}`} className="mt-10 border-t border-[#D9B47A]/15 pt-8">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">{kind === "full" ? "Full" : "New"} moon practice</p>
      <h2 className="mt-3 font-display text-2xl leading-snug"><Gold text={p.heading} /></h2>
      <ol className="mt-5 space-y-4">
        {p.steps.map((s, k) => (
          <li key={k} className="grid grid-cols-[auto_1fr] gap-4 text-[15px] text-[#CFC2B6] leading-relaxed">
            <span className="font-tt-mono text-[12px] text-[#D9B47A] pt-0.5">{pad(k + 1)}</span>
            {s}
          </li>
        ))}
      </ol>
      <p className="mt-5 font-display italic text-xl text-[#D9B47A]">{p.farewell}</p>
    </section>
  );
};

const AppMoon = () => (
  <Page testId="app-moon" eyebrow="Moon practices" title={<>Live by the <em className="text-[#D9B47A]">moon.</em></>}>
    <TonightCard />
    <div className="mt-6"><MoonCalendar /></div>
    <Link to="/app/journal#intentions" data-testid="app-moon-intentions" className={`${ghost} mt-6`}>Your intention journal</Link>
    <Practice kind="new" />
    <Practice kind="full" />
  </Page>
);

// "13 Gates to Ecstasy": the first installable app, at /app.
export default function TempleApp() {
  return (
    <SpaceContext.Provider value={SPACES.gates}>
    <AppFrame app={APPS.gates} tabs={TABS}>
      <Route index element={<AppHome />} />
      <Route path="gates" element={<AppGates />} />
      <Route path="gate/:g" element={<QuantumGate />} />
      <Route path="gates/:slug" element={<div className="pb-24"><GatePage /></div>} />
      <Route path="field" element={<AppField />} />
      <Route path="journal" element={<div className="pb-24"><GatesKept /><Journey /><BreathMapsJournal /><ProgressPanel /></div>} />
      <Route path="us" element={<OurField defaultKind="breath" />} />
      <Route path="moon" element={<AppMoon />} />
      <Route path="ariel" element={<ArielChat />} />
      <Route path="breath" element={<AnkhBreath />} />
      <Route path="breath/partner" element={<PartnerBreathLobby />} />
      <Route path="breath/partner/:code" element={<PartnerBreathSession />} />
      <Route path="body" element={<GatesBodyMap />} />
      <Route path="*" element={<AppHome />} />
    </AppFrame>
    </SpaceContext.Provider>
  );
}
